import { KeyValue } from '@angular/common';
import {
  Component,
  ContentChildren,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { ValidationErrorDirective } from '@app/shared/directives/validation-error.directive';
import { toMap } from '@app/utils/common';
import { loggerFor } from '@app/utils/logger';
import { translateInstant } from '@app/utils/translate';
import { ReservedValidationKeys, ValidationErrorMap } from './validation-error-map';

@Component({
  selector: 'portal-validation-errors',
  templateUrl: './validation-errors.component.html',
  styles: [],
  standalone: false,
})
export class ValidationErrorsComponent implements OnChanges, OnInit {
  @Input()
  alertClass: string = '';

  @Input()
  dirty: boolean;

  @Input()
  displayAsAlert: boolean = false;

  @Input()
  errors: ValidationErrorMap;

  @Input()
  ignoreErrors: string[] = [];

  @Input()
  validationErrors: QueryList<ValidationErrorDirective>;

  @ContentChildren(ValidationErrorDirective)
  validationErrorsChildren: QueryList<ValidationErrorDirective>;

  customErrors: KeyValue<string, any>[] = [];
  showErrors: boolean = false;

  private logger = loggerFor('ValidationErrors');

  private get allErrorDirectives(): ValidationErrorDirective[] {
    const directives = [];

    if (this.validationErrors != null) {
      directives.push(...this.validationErrors.toArray());
    }

    if (this.validationErrorsChildren != null) {
      directives.push(...this.validationErrorsChildren.toArray());
    }

    return directives;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('errors' in changes) {
      const errors = changes.errors.currentValue;

      this.updateErrors(errors);
    }
  }

  ngOnInit(): void {}

  public customMessage(name: string): TemplateRef<any> | undefined {
    const maybeCustomMessage = this.allErrorDirectives.find(
      (validationError) => validationError.portalValidationError === name
    );

    return maybeCustomMessage?.templateRef;
  }

  public translateAnyOfRequiredValues(values: string[]) {
    const translatorFunction = translateInstant('common.fields.');

    return values.map(translatorFunction).join(', ');
  }

  private getCustomErrors(
    errors?: ValidationErrorMap,
    ignore: Record<string, any> = {}
  ): KeyValue<string, any>[] {
    const customErrors: KeyValue<string, any>[] = [];

    if (errors != null) {
      const keys = Object.keys(errors);

      // add "custom" validation error to `customErrors` (backwards compatibility)
      if (errors['custom'] !== undefined) {
        customErrors.push({
          key: errors.custom,
          value: errors.customParameters,
        });
      }

      for (const key of keys) {
        if (ReservedValidationKeys[key] === undefined && ignore[key] === undefined) {
          const error = this.errors[key];

          if (typeof error === 'object') {
            customErrors.push({
              key: error.key,
              value: error.parameters,
            });
          } else {
            this.logger.warn(
              `The error ${key} does not follow the default style ({ key: string, parameters?: object}) and therefore cannot be translated.`
            );
          }
        }
      }
    }

    return customErrors;
  }

  private updateErrors(errors: ValidationErrorMap): void {
    const numberOfErrors = errors ? Object.keys(errors).length : 0;

    if (numberOfErrors > 0) {
      const errorsToIgnore = this.ignoreErrors ?? [];
      const ignoreMap = toMap(errorsToIgnore, (key) => key);
      const customErrors = this.getCustomErrors(errors, ignoreMap);
      const filteredErrors = customErrors.filter(
        (error) => errorsToIgnore.some((ignored) => ignored === error.key) === false
      );

      const hasErrors = numberOfErrors > 0;
      const hasAnyNonCustomError =
        (customErrors.length === numberOfErrors && filteredErrors.length === 0) === false;

      this.customErrors = filteredErrors;
      this.showErrors = hasErrors && hasAnyNonCustomError;
    } else {
      this.customErrors = [];
      this.showErrors = false;
    }
  }
}
